import { Form } from 'antd';
import { has, merge, omit } from 'lodash-es';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { UnitSystem } from '../../../app/enums/measuringUnits';
import { SettingsContext } from '../../../components/domainSpecific/settingsElements';
import { isAviationMode } from '../shipmentModes';

export function mapFormValuesToDependencies(formValues) {
  const { account, origin, destination, serviceInformation, unitSystem } =
    formValues || {};

  return {
    ...formValues,
    // Synthetic values used for dependencies only
    accountNumber: account?.number,
    pickupDateTime: origin?.pickupDateTime,
    deliveryDateTime: destination?.deliveryDateTimeAsap
      ? ''
      : destination?.deliveryDateTime,
    serviceInformation: {
      ...serviceInformation,
      // Virtual field used as a dependency for some BE endpoints
      isMetric: unitSystem === UnitSystem.METRIC,
    },
  };
}

export default function useNewShipmentFormState() {
  const [account, setAccount] = useState();
  const [values, setValues] = useState({});
  const { unitSystem: parentUnitSystem, loading: loadingUnitSystem } =
    useContext(SettingsContext);
  const [unitSystemOverride, setUnitSystemOverride] = useState();

  // We don't want to allow changing unit system in the middle of creating shipment,
  // therefore we capture the initial value
  const [initialParentUnitSystem, setInitialParentUnitSystem] = useState(null);
  useEffect(() => {
    if (!loadingUnitSystem && !initialParentUnitSystem) {
      setInitialParentUnitSystem(parentUnitSystem);
    }
  }, [initialParentUnitSystem, loadingUnitSystem, parentUnitSystem]);

  // Refresh may be called immediately after a sequence of setters and we want it
  // to work with the newest possible values
  const currentState = useRef({ account, values, unitSystemOverride });

  // Keep in sync with steps - hooks cannot be called inside loops
  const [form1] = Form.useForm();
  const form1Ref = useRef();
  const [form2] = Form.useForm();
  const form2Ref = useRef();
  const [form3] = Form.useForm();
  const form3Ref = useRef();
  const [form4] = Form.useForm();
  const form4Ref = useRef();
  const forms = [
    { form: form1, name: 'origin', formRef: form1Ref },
    { form: form2, name: 'destination', formRef: form2Ref },
    { form: form3, name: 'serviceInformation', formRef: form3Ref },
    { form: form4, name: 'packageList', formRef: form4Ref },
  ];

  // Extracts the current form values, contains all the data required for submit
  const extractValues = useCallback(
    overrides =>
      mapFormValuesToDependencies({
        account: currentState.current.account,
        origin: form1.getFieldsValue(true),
        destination: form2.getFieldsValue(),
        serviceInformation: form3.getFieldsValue(),
        packageList: form4.getFieldsValue(),
        unitSystem:
          currentState.current.unitSystemOverride || initialParentUnitSystem,
        ...overrides,
      }),
    [form1, form2, form3, form4, initialParentUnitSystem]
  );

  const refreshValues = useCallback(
    overrides => {
      const newValues = {
        ...currentState.current.values,
        ...extractValues(overrides),
      };
      setValues(newValues);
      currentState.current.values = newValues;
      return newValues;
    },
    [extractValues]
  );

  const setFormDefaultValues = useCallback(
    acc => {
      const isAero = isAviationMode(acc);
      const isAirRetrieval = has(form1.getFieldsValue(), 'flight');
      let defaults = {};

      // For Aero Accounts NF and CIVIL AIRCRAFT PART(S)
      if (isAero) {
        defaults = {
          ...defaults,
          serviceType: { code: 'NF', text: 'NEXT FLIGHT (DOOR-TO-DOOR)' },
          commodity: 'CIVIL AIRCRAFT PART(S)',
        };
      }

      // Set AirRetrievals defaults
      if (isAirRetrieval) {
        defaults = {
          ...defaults,
          serviceType: { code: 'DRR', text: 'RETRIEVAL' },
        };
      }

      const newValues = merge({}, defaults, form3.getFieldsValue());
      form3.setFieldsValue(newValues);
    },
    // eslint-disable-next-line
    [account]
  );

  return {
    forms,
    refreshValues,
    account,
    setAccount: acc => {
      setAccount(acc);
      currentState.current.account = acc;
      setFormDefaultValues(acc);
      refreshValues();
    },
    unitSystem: unitSystemOverride || initialParentUnitSystem,
    setUnitSystem: us => {
      setUnitSystemOverride(us);
      currentState.current.unitSystemOverride = us;
      refreshValues();
    },
    values,
    extractValues,
  };
}

export function useNewShipmentFormStateSingleForm() {
  const [account, setAccount] = useState();
  const [values, setValues] = useState({});
  const { unitSystem: parentUnitSystem, loading: loadingUnitSystem } =
    useContext(SettingsContext);
  const [unitSystemOverride, setUnitSystemOverride] = useState();

  // We don't want to allow changing unit system in the middle of creating shipment,
  // therefore we capture the initial value
  const [initialParentUnitSystem, setInitialParentUnitSystem] = useState(null);
  useEffect(() => {
    if (!loadingUnitSystem && !initialParentUnitSystem) {
      setInitialParentUnitSystem(parentUnitSystem);
    }
  }, [initialParentUnitSystem, loadingUnitSystem, parentUnitSystem]);

  // Refresh may be called immediately after a sequence of setters and we want it
  // to work with the newest possible values
  const currentState = useRef({ account, values, unitSystemOverride });

  // Keep in sync with steps - hooks cannot be called inside loops
  const [form1] = Form.useForm();
  const form1Ref = useRef();
  const forms = [
    { form: form1, name: 'shipmentForm', formRef: form1Ref, current: form1Ref },
  ];

  // Extracts the current form values, contains all the data required for submit
  const extractValues = useCallback(
    overrides => {
      const formValues = form1.getFieldsValue();
      return mapFormValuesToDependencies({
        account: currentState.current.account,
        ...omit(form1.getFieldsValue(true), 'packages'),
        packageList: {
          packages: formValues?.packages,
          tbd: formValues?.tbd,
        },
        unitSystem:
          currentState.current.unitSystemOverride || initialParentUnitSystem,
        ...overrides,
      });
    },
    [form1, initialParentUnitSystem]
  );

  const refreshValues = useCallback(
    overrides => {
      const newValues = {
        ...currentState.current.values,
        ...extractValues(overrides),
      };
      setValues(newValues);
      currentState.current.values = newValues;
      return newValues;
    },
    [extractValues]
  );

  const setFormDefaultValues = useCallback(
    acc => {
      const isAero = isAviationMode(acc);
      const isAirRetrieval = has(form1.getFieldsValue(), 'flight');
      let defaults = {};

      // For Aero Accounts NF and CIVIL AIRCRAFT PART(S)
      if (isAero) {
        defaults = {
          ...defaults,
          serviceInformation: {
            serviceType: { code: 'NF', text: 'NEXT FLIGHT (DOOR-TO-DOOR)' },
            commodity: 'CIVIL AIRCRAFT PART(S)',
          },
        };
      }

      // Set AirRetrievals defaults
      if (isAirRetrieval) {
        defaults = {
          ...defaults,
          serviceInformation: {
            serviceType: { code: 'DRR', text: 'RETRIEVAL' },
          },
        };
      }

      const newValues = merge({}, defaults, form1.getFieldsValue());
      form1.setFieldsValue(newValues);
    },
    // eslint-disable-next-line
    [account]
  );

  return {
    forms,
    refreshValues,
    account,
    setAccount: acc => {
      setAccount(acc);
      currentState.current.account = acc;
      setFormDefaultValues(acc);
      refreshValues();
    },
    unitSystem: unitSystemOverride || initialParentUnitSystem,
    setUnitSystem: us => {
      setUnitSystemOverride(us);
      currentState.current.unitSystemOverride = us;
      refreshValues();
    },
    values,
    extractValues,
  };
}
